/**
 * 웹에서 지원하는 라우트 패턴 (single source of truth).
 * - checkWebPageExists: URL 이동 전 페이지 존재 여부 확인
 * - 각 패키지 Navigator: 여기서 path를 import하여 ScreenItem에 사용
 *
 * 새 페이지 추가 시:
 * 1. 여기에 path 추가
 * 2. 해당 패키지 Navigator.tsx에서 import하여 ScreenItem 추가
 */
export const WEB_ROUTE_PATHS = {
  Home: '/',
  GoodsDetail: '/goods/:goodsId',
  GoodsList: '/goods-list',
  MarketHome: '/markets/:marketId',
  AlarmSetting: '/mypage/alarm-setting',
  Laboratory: '/mypage/laboratory',
  PointOfContact: '/mypage/point-of-contact',
  MyPersonalization: '/mypage/personalization',
  Setting: '/mypage/setting',
  MyInfo: '/mypage/my-info',
  RecentlyViewedGoods: '/mypage/recently-viewed-goods',
  EditGoodsLike: '/like/edit',
  FolderDetail: '/like/folders/:folderSno',
  SearchHelper: '/search/search-helper',
  SearchResult: '/search/search-result',
  Screens: '/screens',
  FragmentScreens: '/screens/fragment',
  ServiceMap: '/overview/icons/all',
} as const;

export type WebRoutePath =
  (typeof WEB_ROUTE_PATHS)[keyof typeof WEB_ROUTE_PATHS];

const routeRegexes = Object.values(WEB_ROUTE_PATHS).map(pattern => {
  const regexStr = '^' + pattern.replace(/:[\w]+/g, '[^/]+') + '$';
  return new RegExp(regexStr);
});

export const isKnownRoute = (path: string): boolean => {
  if (/\.\w+$/.test(path)) {
    return true;
  }
  const normalizedPath =
    path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  return routeRegexes.some(regex => regex.test(normalizedPath));
};

export type ScreenName = keyof typeof WEB_ROUTE_PATHS;

export type ResolvedRoute = {
  screen: ScreenName;
  /** path의 동적 세그먼트 값 (예: /goods/:goodsId → { goodsId: '123' }) */
  pathParams: Record<string, string>;
  /** 쿼리 파라미터 */
  queryParams: Record<string, string>;
};

/**
 * webLink path에서 screen name과 파라미터를 추출합니다.
 *
 * 예시:
 * - '/goods/123?ref=home'
 *   → { screen: 'GoodsDetail', pathParams: { goodsId: '123' }, queryParams: { ref: 'home' } }
 * - '/' → { screen: 'Home', pathParams: {}, queryParams: {} }
 * - 매칭되는 패턴이 없으면 null
 */
export const resolveScreenFromPath = (path: string): ResolvedRoute | null => {
  const [rawPath, rawQuery = ''] = path.split('?');
  const normalized =
    rawPath.endsWith('/') && rawPath !== '/' ? rawPath.slice(0, -1) : rawPath;

  const queryParams: Record<string, string> = {};
  if (rawQuery) {
    new URLSearchParams(rawQuery).forEach((value, key) => {
      queryParams[key] = value;
    });
  }

  for (const [screenKey, pattern] of Object.entries(WEB_ROUTE_PATHS)) {
    const paramNames: string[] = [];
    const regexStr =
      '^' +
      pattern.replace(/:(\w+)/g, (_, name) => {
        paramNames.push(name);
        return '([^/]+)';
      }) +
      '$';
    const match = normalized.match(new RegExp(regexStr));
    if (match) {
      const pathParams: Record<string, string> = {};
      paramNames.forEach((name, i) => {
        pathParams[name] = decodeURIComponent(match[i + 1]);
      });
      return {
        screen: screenKey as ScreenName,
        pathParams,
        queryParams,
      };
    }
  }
  return null;
};
